import React, { useState, useEffect } from "react";
import { useChat } from '../App'; // Adjust the import path based on your file structure

import enrollPic from "../image/enroll.svg";
import {
  EnvelopeIcon,
  PhoneIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowPathIcon,
  UserIcon,
} from "@heroicons/react/24/solid";

import { Phone, MessageSquare } from "lucide-react";

const SupportSection = ({ onViewFaqClick, onRequestCallBackClick }) => {
  const { openChat } = useChat();
  const [guestId, setGuestId] = useState(null);

  // Load guestId from localStorage on component mount
  useEffect(() => {
    const savedGuestId = localStorage.getItem('guestId');
    if (savedGuestId) {
      setGuestId(savedGuestId);
    }
  }, []);

  // Generate or retrieve guestId
  const getGuestId = () => {
    let id = guestId;
    if (!id) {
      // Generate a simple guest ID if not exists
      id = 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('guestId', id);
      setGuestId(id);
    }
    return id;
  };

  // Handle Chat With Us button click
  const handleChatWithUs = () => {
    // Get guestId if user is not logged in
    // Replace this with your actual authentication check
    const isLoggedIn = false; // Example: Change this based on your auth state

    if (!isLoggedIn) {
      const guestId = getGuestId();
      // Pass guestId to openChat function
      openChat({ isGuest: true, guestId });
    } else {
      openChat(); // For logged-in users
    }
  };

  const studentSupportItems = [
    {
      title: "Weekly Live Doubt-Clearing Sessions",
      description:
        "Participate in weekly online sessions where industry experts address questions, clarify concepts, and provide practical guidance on implementation.",
      highlight: false,
    },
    {
      title: "Dedicated Learning Support",
      description:
        "Receive assistance with course navigation, assignments, project work, and learning progress throughout your journey.",
      highlight: false,
    },
    {
      title: "AI-Powered Learning Assistance",
      description:
        "Get instant support through our AI-enabled learning platform to access information, explanations, and learning resources whenever needed.",
      highlight: false,
    },
    {
      title: "Business & Implementation Guidance",
      description:
        "Gain practical insights and recommendations to help you launch, manage, and scale your e-commerce business effectively.",
      highlight: false,
    },
    {
      title: "Community Learning Network",
      description:
        "Connect with fellow entrepreneurs, business owners, professionals, and students to share experiences, ideas, and growth opportunities.",
      highlight: false,
    },
    {
      title: "Lifetime Access to Learning Resources",
      description:
        "Revisit lessons, tools, templates, and future course updates anytime through the learning platform, ensuring continuous learning and business growth.",
      highlight: false,
    },
  ];

  const contactOptions = [
    {
      icon: <EnvelopeIcon className="w-10 h-10 text-blue-500" />,
      title: "Email Us",
      subtitle: "Get response within 24 hours",
      link: "info@kingster.edu.in",
      additionalInfo: "contact@kingster.edu.in",
    },
    {
      icon: <PhoneIcon className="w-10 h-10 text-blue-500" />,
      title: "Call Us",
      subtitle: "Available during Office Hours",
      link: "+91 7356255005",
      additionalInfo: "0471 2334003",
    },
    {
      icon: <QuestionMarkCircleIcon className="w-10 h-10 text-blue-500" />,
      title: "Browse FAQs",
      subtitle: "Find quick answers",
      link: "View faqs",
      isFaq: true,
    },
  ];

  const whyChoose = [
    {
      icon: <ClockIcon className="w-12 h-12 text-blue-500" />,
      title: "14/7 Availability",
      description: "Round the clock support whenever you need",
    },
    {
      icon: <UserGroupIcon className="w-12 h-12 text-blue-500" />,
      title: "Expert Counselors",
      description: "Experienced professionals at your service",
    },
    {
      icon: <ArrowPathIcon className="w-12 h-12 text-blue-500" />,
      title: "Quick Response",
      description: "Fast and efficient problem resolution",
    },
    {
      icon: <UserIcon className="w-12 h-12 text-blue-500" />,
      title: "Personalized Help",
      description: "Tailored solution to your needs",
    },
  ];

  // Handle FAQ click
  const handleFaqClick = () => {
    console.log("FAQ link clicked");
    if (onViewFaqClick) {
      console.log("Calling onViewFaqClick");
      onViewFaqClick();
    } else {
      console.log("onViewFaqClick is not provided");
    }
  };

  // Handle Request Call Back
  const handleRequestCallBack = () => {
    if (onRequestCallBackClick) {
      onRequestCallBackClick();
    }
  };

  return (
    <div className="bg-white">
      {/* Student Support Section - Cards now taller and less wide */}
      <section className="px-4 md:px-12 lg:px-16 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2
            className="
    font-[Figtree]
    font-semibold

    text-[34px]
    sm:text-[42px]
    lg:text-[55px]

    leading-[150%]

    tracking-[-0.03em]

    text-center

    text-[#02054C]

    mb-12
  "
          >
            Student Support
          </h2>

          <p className="font-[Figtree] font-medium text-[19px] leading-[21px] tracking-[0em] text-[#818285] text-center mt-2 max-w-2xl mx-auto">
            We are committed to helping every learner successfully complete the program and confidently implement their e-commerce business goals through continuous guidance, mentorship, and support.
          </p>
        </div>

        {/* Student Support Grid - Responsive */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {studentSupportItems.map((item, index) => (
            <div
              key={index}
              className="group rounded-2xl border border-blue-700 p-6 md:p-8 h-full min-h-[200px] md:min-h-[220px] flex flex-col transition duration-300 hover:bg-blue-500 hover:border-blue-500"
            >
              <h3 className="font-[Figtree] font-semibold text-[24px] md:text-[30px] leading-[1.1] tracking-[-0.04em] text-[#1E1E1E] mb-3 group-hover:text-white">
                {item.title}
              </h3>

              <p className="font-[Figtree] font-medium text-[16px] md:text-[19px] leading-[1.5] tracking-[-0.04em] text-[#1E1E1E] group-hover:text-white flex-grow">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions Section */}

      <section className="py-16 px-4 sm:px-6 lg:px-16 bg-[#F8F8F8] max-w-[1500px] w-full mx-auto">

        {/* Heading */}

        <div className="text-center">

          <h2 className="font-[Figtree] font-semibold text-[34px] sm:text-[42px] lg:text-[55px] leading-[150%] tracking-[-0.03em] text-[#02054C]">

            Still Have Questions

          </h2>


          <p className="font-[Figtree] font-medium text-[18px] sm:text-[22px] lg:text-[28px] leading-[150%] text-[#818285] mt-2">

            Our Counselors are here to help you make the right choice

          </p>

        </div>



        {/* Buttons */}

        <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mt-10 mb-16">


          {/* Request Callback */}

          <button
            type="button"
            onClick={handleRequestCallBack}
            className="w-[280px] h-[72px] rounded-[100px] p-[8px] pr-[24px] flex items-center gap-[10px] border border-[#D8D8D8] shadow-sm hover:scale-[1.03] transition-all duration-300"
            style={{
              background:
                "linear-gradient(94.44deg, #1D83F8 1.62%, #114D92 78.57%)",
            }}
          >

            <div className="w-[56px] h-[56px] rounded-full bg-white flex items-center justify-center shrink-0">

              <Phone
                className="w-6 h-6 text-[#1D83F8]"
                strokeWidth={2.5}
              />

            </div>


            <span className="font-[Figtree] font-medium text-[20px] leading-[150%] text-white">

              Request Call Back

            </span>

          </button>





          {/* Chat With Us */}

          <button
            type="button"
            onClick={handleChatWithUs}
            className="w-[280px] h-[72px] rounded-[100px] p-[8px] pr-[24px] flex items-center gap-[10px] border border-[#D8D8D8] shadow-sm hover:scale-[1.03] transition-all duration-300"
            style={{
              background:
                "linear-gradient(94.44deg, #1D83F8 1.62%, #114D92 78.57%)",
            }}
          >

            <div className="w-[56px] h-[56px] rounded-full bg-white flex items-center justify-center shrink-0">

              <MessageSquare
                className="w-6 h-6 text-[#1D83F8]"
                strokeWidth={2.5}
              />

            </div>


            <span className="font-[Figtree] font-medium text-[20px] leading-[150%] text-white">

              Chat With Us

            </span>

          </button>

        </div>




        {/* Contact Cards */}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1200px] mx-auto">

          {contactOptions.map((item, index) => (

            <div
              key={index}
              className="bg-white border border-[#DDE7F7] rounded-[28px] p-8 flex flex-col items-center text-center min-h-[260px] hover:shadow-lg transition-all duration-300"
            >

              {/* Icon */}

              <div className="w-[82px] h-[82px] rounded-full bg-[#EEF5FF] flex items-center justify-center mb-6">

                <div className="text-[#1D83F8] scale-[1.3]">

                  {item.icon}

                </div>

              </div>



              {/* Title */}

              <h3 className="font-[Figtree] font-semibold text-[26px] leading-[125%] tracking-[0] text-[#02054C]">

                {item.title}

              </h3>



              {/* Subtitle */}

              <p className="mt-3 font-[Figtree] text-[17px] leading-[150%] text-[#818285]">

                {item.subtitle}

              </p>




              {/* Bottom Content */}

              {item.title === "Email Us" && item.additionalInfo ? (

                <div className="mt-auto pt-6">

                  <p className="text-[#1D83F8] font-semibold text-[16px] break-all">

                    {item.link}

                  </p>

                  <p className="text-[#1D83F8] font-semibold text-[16px] break-all mt-1">

                    {item.additionalInfo}

                  </p>

                </div>

              ) : item.title === "Call Us" && item.additionalInfo ? (

                <div className="mt-auto pt-6">

                  <p className="text-[#1D83F8] font-semibold text-[18px]">

                    {item.link}

                  </p>

                  <p className="text-[#1D83F8] font-semibold text-[18px] mt-1">

                    {item.additionalInfo}

                  </p>

                </div>

              ) : item.isFaq ? (

                <button
                  onClick={handleFaqClick}
                  className="mt-auto pt-6 text-[#1D83F8] font-semibold text-[16px] hover:underline"
                >

                  {item.link}

                </button>

              ) : (

                <p className="mt-auto pt-6 text-[#1D83F8] font-semibold text-[16px]">

                  {item.link}

                </p>

              )}

            </div>

          ))}

        </div>

      </section>
    </div>
  );
};

export default SupportSection;